import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import * as React from "react";
import { cn } from "@/lib/utils";
import { Check, CircleIcon } from "lucide-react";
import { toggleStyle } from "./toggle";

type ToggleGroupProps = React.ComponentPropsWithoutRef<
  typeof ToggleGroupPrimitive.Root
>;

const ToggleGroupContext =
  React.createContext<ToggleGroupProps["type"]>("single");

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  ToggleGroupProps
>(({ className, children, ...props }, ref) => {
  return (
    <ToggleGroupPrimitive.Root
      ref={ref}
      className={cn("flex flex-row flex-wrap  gap-2.5 gap-y-1.5", className)}
      {...props}
    >
      <ToggleGroupContext.Provider value={props.type}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
});

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item>
>(({ className, children, ...props }, ref) => {
  const type = React.useContext(ToggleGroupContext);

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(toggleStyle(), className)}
      {...props}
    >
      {children}
      {type === "multiple" && (
        <div className="h-4 w-4 flex-shrink-0 ml-auto border rounded flex justify-center items-center group-data-[state='on']/root:bg-header-foreground group-data-[state='on']/root:border-header-foreground">
          <Check className="h-4 w-4 group-data-[state='off']/root:hidden text-primary" />
        </div>
      )}
      {type === "single" && (
        <div className="h-6 w-6 flex-shrink-0 ml-auto border rounded-full flex justify-center items-center group-data-[state='on']/root:border-header-foreground">
          <CircleIcon className="h-4 w-4 group-data-[state='off']/root:hidden text-primary fill-white" />
        </div>
      )}
    </ToggleGroupPrimitive.Item>
  );
});

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export { ToggleGroup, ToggleGroupItem };
